import posthog from 'posthog-js'

// CRITICAL: Override ALL console methods BEFORE PostHog initialization to prevent sensitive data logging
if (typeof window !== 'undefined') {
  // Store original console methods
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;
  
  // NUCLEAR OPTION: Block ALL PostHog logs except critical errors
  console.log = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('[PostHog.js]')) {
      // Allow only essential PostHog logs (like connection issues)
      const allowedMessages = [
        'Failed to fetch',
        'Error loading',
        'Connection failed'
      ];
      
      const isAllowed = allowedMessages.some(msg => args[0].includes(msg));
      if (!isAllowed) {
        return; // Block all other PostHog logs
      }
    }
    originalLog(...args);
  };
  
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('[PostHog.js]')) {
      return; // Block ALL PostHog warnings
    }
    originalWarn(...args);
  };
  
  console.error = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('[PostHog.js]')) {
      // Only allow actual errors, not data logs
      const isRealError = args[0].includes('Error') || args[0].includes('Failed');
      if (!isRealError) {
        return; // Block non-error PostHog logs
      }
    }
    originalError(...args);
  };
}

// Initialize PostHog with MAXIMUM security settings
if (typeof window !== 'undefined') {
  posthog.init(
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY || 'phc_test_key',
    {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only', // Only create profiles for identified users
      debug: false, // EXPLICITLY disable debug mode
      verbose: false, // Disable verbose logging
      disable_session_recording: true, // Extra security - disable session recording
      loaded: (posthog) => {
        // FORCE disable all debug modes
        posthog.config.debug = false;
        posthog.config.verbose = false;
        
        // Override PostHog's internal logging if it exists
        if (posthog._send_request) {
          const originalSendRequest = posthog._send_request;
          posthog._send_request = function(...args) {
            // Call original but don't log
            return originalSendRequest.apply(this, args);
          };
        }
      },
      capture_pageview: false, // We'll manually capture pageviews in React Router
      capture_pageleave: true, // Automatically capture when users leave pages
    }
  )
}

export { posthog }

// Utility functions for common tracking events
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    posthog.capture(eventName, properties)
  }
}

export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    posthog.identify(userId, properties)
  }
}

export const trackPageView = (pathname?: string) => {
  if (typeof window !== 'undefined') {
    posthog.capture('$pageview', {
      $current_url: pathname || window.location.pathname
    })
  }
} 